import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import { getValidAccessToken } from 'src/store/oauth-browser';

interface ProviderInstance {
  doc: Y.Doc;
  wsProvider?: WebsocketProvider;
  persistence: IndexeddbPersistence;
  notesText: Y.Text;
  propertiesMap: Y.Map<any>;
  refCount: number;
}

/**
 * Centralized manager for viewer properties WebSocket connections and Yjs documents.
 * This ensures we don't create duplicate connections and properly manage lifecycle.
 */
class ViewerPropertiesManager {
  private providers: Map<string, ProviderInstance> = new Map();
  private initPromises: Map<string, Promise<ProviderInstance>> = new Map();

  /**
   * Get or create a provider for the given configuration.
   * Multiple components can share the same provider.
   */
  async getProvider(
    organizationId: string,
    entityType: string,
    entityId: string,
    userId: string,
  ): Promise<{ notesText: Y.Text; cleanup: () => void }> {
    const key = `${organizationId}/${entityType}/${entityId}`;

    console.log('[ViewerPropertiesManager] getProvider called for:', key, {
      activeProviders: this.providers.size,
      pendingInits: this.initPromises.size,
    });

    // Check if we already have this provider
    let provider = this.providers.get(key);

    if (provider) {
      console.log('[ViewerPropertiesManager] Reusing existing provider for:', key, 'refCount:', provider.refCount + 1);
      provider.refCount++;
      return {
        notesText: provider.notesText,
        cleanup: () => {
          console.log('[ViewerPropertiesManager] Cleanup function called for:', key);
          this.releaseProvider(key);
        },
      };
    }

    // Check if we're already initializing this provider
    let initPromise = this.initPromises.get(key);

    if (initPromise) {
      console.log('[ViewerPropertiesManager] Waiting for existing initialization for:', key);
      provider = await initPromise;
      provider.refCount++;
      return {
        notesText: provider.notesText,
        cleanup: () => {
          console.log('[ViewerPropertiesManager] Cleanup function called for:', key);
          this.releaseProvider(key);
        },
      };
    }

    // Create new provider
    console.log('[ViewerPropertiesManager] Creating new provider for:', key);
    initPromise = this.createProvider(organizationId, entityType, entityId, userId, key);
    this.initPromises.set(key, initPromise);

    try {
      provider = await initPromise;
      this.providers.set(key, provider);
      this.initPromises.delete(key);

      return {
        notesText: provider.notesText,
        cleanup: () => {
          console.log('[ViewerPropertiesManager] Cleanup function called for:', key);
          this.releaseProvider(key);
        },
      };
    } catch (error) {
      console.error('[ViewerPropertiesManager] Failed to create provider for:', key, error);
      this.initPromises.delete(key);
      throw error;
    }
  }

  /**
   * Create a new provider instance
   */
  private async createProvider(
    _organizationId: string,
    _entityType: string,
    _entityId: string,
    userId: string,
    key: string,
  ): Promise<ProviderInstance> {
    console.log('[ViewerPropertiesManager] Starting provider creation for:', key);

    // Create Y.Doc with unique guid
    const doc = new Y.Doc({ guid: key });

    // Set up local IndexedDB persistence with unique database per entity
    // Important: Include full key in dbName to isolate data between entities
    const dbName = `viewer-properties-${userId}-${key.replace(/\//g, '-')}`;
    const persistence = new IndexeddbPersistence(dbName, doc);

    // Wait for data to be loaded from IndexedDB
    await persistence.whenSynced;
    console.log('[ViewerPropertiesManager] IndexedDB synced for:', key);

    // Use the doc's root map directly for viewer properties
    const propertiesMap = doc.getMap();

    // Get or create the notes Text field
    let notesText = propertiesMap.get('notes') as Y.Text;
    if (!notesText) {
      console.log('[ViewerPropertiesManager] Creating new notes Y.Text for:', key);
      notesText = new Y.Text();
      propertiesMap.set('notes', notesText);
    } else {
      console.log('[ViewerPropertiesManager] Found existing notes for:', key, 'content:', notesText.toString());
    }

    // Try to set up WebSocket provider with authentication
    let wsProvider: WebsocketProvider | undefined;
    try {
      const authToken = await getValidAccessToken();

      if (authToken) {
        const wsUrl = 'wss://api.trinnvis.io/ws/notes';
        const roomName = key; // Use the key as room name

        console.log('[ViewerPropertiesManager] Creating WebSocket for:', key);

        // Create WebSocket provider with authentication
        wsProvider = new WebsocketProvider(wsUrl, roomName, doc, {
          params: {
            token: authToken,
          },
          resyncInterval: 5000,
          maxBackoffTime: 30000,
          disableBc: false, // Enable BroadcastChannel for cross-tab synchronization
        });

        // Store event handlers so we can remove them later
        const statusHandler = (event: { status: string }) => {
          console.log('[ViewerPropertiesManager] WebSocket status:', event.status, 'for:', key);
        };

        const syncHandler = (isSynced: boolean) => {
          console.log('[ViewerPropertiesManager] WebSocket sync:', isSynced, 'for:', key);
        };

        const errorHandler = (event: Event) => {
          console.error('[ViewerPropertiesManager] WebSocket error for:', key, event);
        };

        const closeHandler = (event: CloseEvent | null) => {
          if (event) {
            console.error('[ViewerPropertiesManager] WebSocket closed for:', key, {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            });
          }
        };

        // Attach event handlers
        wsProvider.on('status', statusHandler);
        wsProvider.on('sync', syncHandler);
        wsProvider.on('connection-error', errorHandler);
        wsProvider.on('connection-close', closeHandler);

        // Store handlers on the provider for cleanup
        (wsProvider as any)._statusHandler = statusHandler;
        (wsProvider as any)._syncHandler = syncHandler;
        (wsProvider as any)._errorHandler = errorHandler;
        (wsProvider as any)._closeHandler = closeHandler;
      } else {
        console.warn('[ViewerPropertiesManager] No auth token available for:', key);
      }
    } catch (error) {
      console.warn('[ViewerPropertiesManager] Failed to create WebSocket for:', key, error);
    }

    return {
      doc,
      wsProvider,
      persistence,
      notesText,
      propertiesMap,
      refCount: 1,
    };
  }

  /**
   * Release a provider. Decrements ref count and destroys if no longer needed.
   */
  private releaseProvider(key: string) {
    const provider = this.providers.get(key);

    if (!provider) {
      console.warn('[ViewerPropertiesManager] Trying to release non-existent provider:', key);
      return;
    }

    provider.refCount--;
    console.log('[ViewerPropertiesManager] Released provider for:', key, {
      refCount: provider.refCount,
      activeProviders: this.providers.size,
    });

    if (provider.refCount <= 0) {
      console.log('[ViewerPropertiesManager] Destroying provider for:', key);

      // Destroy WebSocket provider first to stop all network activity
      if (provider.wsProvider) {
        console.log('[ViewerPropertiesManager] Disconnecting WebSocket for:', key);

        // Stop reconnection attempts immediately
        provider.wsProvider.shouldConnect = false;

        // Disconnect BroadcastChannel first if available
        if (provider.wsProvider.disconnectBc) {
          provider.wsProvider.disconnectBc();
        }

        // Close the WebSocket connection directly if it exists
        if (provider.wsProvider.ws) {
          provider.wsProvider.ws.close();
        }

        // Remove specific event handlers if they exist
        const ws = provider.wsProvider as any;
        if (ws._statusHandler) {
          provider.wsProvider.off('status', ws._statusHandler);
          delete ws._statusHandler;
        }
        if (ws._syncHandler) {
          provider.wsProvider.off('sync', ws._syncHandler);
          delete ws._syncHandler;
        }
        if (ws._errorHandler) {
          provider.wsProvider.off('connection-error', ws._errorHandler);
          delete ws._errorHandler;
        }
        if (ws._closeHandler) {
          provider.wsProvider.off('connection-close', ws._closeHandler);
          delete ws._closeHandler;
        }

        // Clear intervals manually as a safety measure
        if (ws._resyncInterval) {
          clearInterval(ws._resyncInterval);
          ws._resyncInterval = 0;
        }
        if (ws._checkInterval) {
          clearInterval(ws._checkInterval);
        }

        // Disconnect and destroy
        provider.wsProvider.disconnect();
        provider.wsProvider.destroy();

        // Clear reference completely
        delete (provider as any).wsProvider;
      }

      // Destroy persistence
      if (provider.persistence) {
        console.log('[ViewerPropertiesManager] Destroying persistence for:', key);
        provider.persistence.destroy();
      }

      // Destroy document
      if (provider.doc) {
        console.log('[ViewerPropertiesManager] Destroying Y.Doc for:', key);
        provider.doc.destroy();
      }

      // Remove from map
      this.providers.delete(key);
      console.log('[ViewerPropertiesManager] Provider destroyed for:', key, {
        remainingProviders: this.providers.size,
      });
    }
  }

  /**
   * Get current state for debugging
   */
  getState() {
    const state: any = {
      activeProviders: [],
      pendingInits: Array.from(this.initPromises.keys()),
    };

    this.providers.forEach((provider, key) => {
      state.activeProviders.push({
        key,
        refCount: provider.refCount,
        hasWebSocket: !!provider.wsProvider,
        notesContent: provider.notesText.toString(),
      });
    });

    return state;
  }

  /**
   * Clean up all providers (for debugging/testing)
   */
  destroyAll() {
    console.log('[ViewerPropertiesManager] Destroying all providers');

    this.providers.forEach((provider, key) => {
      console.log('[ViewerPropertiesManager] Force destroying:', key);

      if (provider.wsProvider) {
        provider.wsProvider.shouldConnect = false;
        if (provider.wsProvider.disconnectBc) {
          provider.wsProvider.disconnectBc();
        }
        if (provider.wsProvider.ws) {
          provider.wsProvider.ws.close();
        }
        provider.wsProvider.disconnect();
        provider.wsProvider.destroy();
      }

      if (provider.persistence) {
        provider.persistence.destroy();
      }

      if (provider.doc) {
        provider.doc.destroy();
      }
    });

    this.providers.clear();
    this.initPromises.clear();
  }
}

// Export singleton instance
export const viewerPropertiesManager = new ViewerPropertiesManager();

// Export for debugging
(window as any).viewerPropertiesManager = viewerPropertiesManager;
